/**
 * Causal Frontiers and Cursor Synchronization
 */
import { offlineDb } from "@/db/offlineDb";
import { isStaleEvent } from "../domain/staleness";

/**
 * Computes the causal frontier for all aggregates currently known to this device.
 * For M1, the simplest and most robust causal frontier for an aggregate is the 
 * complete Set of known event IDs. Since POS orders are small (dozens of events), 
 * this perfectly captures concurrent sibling branches without complex vector clocks.
 * 
 * Returns: { "aggregate_id": ["event_id_1", "event_id_2"] }
 */
export async function computeLocalFrontiers() {
  const allEvents = await offlineDb.captain_events.toArray();
  const frontiers = {};

  for (const event of allEvents) {
    if (!frontiers[event.aggregate_id]) {
      frontiers[event.aggregate_id] = [];
    }
    frontiers[event.aggregate_id].push(event.event_id);
  }

  return frontiers;
}

/**
 * Given a peer's frontier (what they know), returns an array of events 
 * that we have but they are missing.
 * 
 * peerFrontiers: { "aggregate_id": ["event_id_1"] }
 */
export async function computeMissingEventsForPeer(peerFrontiers) {
  const allOurEvents = await offlineDb.captain_events.toArray();
  const missingEventsToSent = [];

  for (const event of allOurEvents) {
    // Half-day-old captures are never offered: they would only paint a
    // colleague's board CONFLICT (see ingestion.processIncomingEvent).
    if (isStaleEvent(event)) continue;
    const peerAggregateFrontier = peerFrontiers[event.aggregate_id] || [];
    if (!peerAggregateFrontier.includes(event.event_id)) {
      missingEventsToSent.push(event);
    }
  }

  return missingEventsToSent;
}

/**
 * For requesting missing causal parents
 */
export async function getMissingParents(knownEventIds, eventsBatch) {
  const missingParents = new Set();
  const incomingIds = new Set(eventsBatch.map(e => e.event_id));

  for (const event of eventsBatch) {
    if (event.parent_event_id) {
      if (!knownEventIds.has(event.parent_event_id) && !incomingIds.has(event.parent_event_id)) {
        missingParents.add(event.parent_event_id);
      }
    }
  }

  return Array.from(missingParents);
}
